import React, { useState, useEffect } from 'react';
import { battlesApi } from '../../api/battles';
import HrnbButton from '../controls/HrnbButton';
import HrnbForm from '../controls/HrnbForm';
import HrnbInput from '../controls/HrnbInput';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import HrnbDateTimePicker from '../controls/HrnbDateTimePicker';
import HrnbFileUpload from '../controls/HrnbFileUpload';
import HrnbImage from '../controls/HrnbImage';
import { getFormatIcon, getFormatLabel, getFormatDescription, getFormatFileTypes } from '../../utils/battleFormats';
import { SaveIcon, ClearIcon, DeleteIcon } from '../../icons';
import '../../css/components/CreateBattleForm.css';

const TYPE_OPTIONS = [
    { value: 'SINGLE', label: 'Индивидуальный' },
    { value: 'TEAM', label: 'Командный' },
];

const FORMAT_OPTIONS = [
    { value: 'AUDIO', label: '🎵 Аудио' },
    { value: 'TEXT', label: '📝 Текст' },
];

const VISIBILITY_OPTIONS = [
    { value: 'PUBLIC', label: 'Публичный' },
    { value: 'PRIVATE', label: 'Частный' },
    { value: 'INVITE_ONLY', label: 'По приглашениям' },
];

const STATUS_OPTIONS = [
    { value: 'PLANNED', label: 'Запланирован' },
    { value: 'ACTIVE', label: 'Активен' },
    { value: 'FINISHED', label: 'Завершён' },
    { value: 'CANCELLED', label: 'Отменён' },
];

const AGE_RESTRICTION_OPTIONS = [
    { value: '0', label: 'Без ограничений (0+)' },
    { value: '13', label: '13+' },
    { value: '16', label: '16+' },
    { value: '18', label: '18+' },
];

export default function EditBattleForm({ isOpen, onClose, onSuccess, battleId }) {
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'SINGLE',
        format: 'AUDIO',
        status: 'PLANNED',
        visibility: 'PUBLIC',
        ageRestriction: 0,
        startTime: '',
        endTime: '',
        registrationDeadline: '',
        prizePool: '',
        maxParticipants: '',
        coverImageId: null,
        coverFile: null,
        coverPreviewUrl: null,
        tags: [],
        organizers: [],
        judges: [],
        participants: [],
        regulation: {
            rules: [],
            limitations: [],
            formats: null,
            ageLimits: '0+',
        },
    });

    // Загрузка данных баттла при открытии
    useEffect(() => {
        if (isOpen && battleId) {
            fetchBattleData();
        }
        setInternalOpen(isOpen);
    }, [isOpen, battleId]);

    const fetchBattleData = async () => {
        try {
            setFetchLoading(true);
            const data = await battlesApi.get(battleId);
            
            setFormData({
                title: data.title || '',
                description: data.description || '',
                type: data.type || 'SINGLE',
                format: data.format || 'AUDIO',
                status: data.status || 'PLANNED',
                visibility: data.visibility || 'PUBLIC',
                ageRestriction: data.ageRestriction || 0,
                startTime: data.startTime ? data.startTime.replace('T', ' ').substring(0, 16) : '',
                endTime: data.endTime ? data.endTime.replace('T', ' ').substring(0, 16) : '',
                registrationDeadline: data.registrationDeadline ? data.registrationDeadline.replace('T', ' ').substring(0, 16) : '',
                prizePool: data.prizePool || '',
                maxParticipants: data.maxParticipants || '',
                coverImageId: data.coverImageId || null,
                tags: data.tags || [],
                organizers: data.organizers?.map(o => o.id) || [],
                judges: data.judges?.map(j => j.id) || [],
                participants: data.participants?.map(p => p.id) || [],
                regulation: {
                    rules: data.regulation?.rules || [],
                    limitations: data.regulation?.limitations || [],
                    formats: data.regulation?.formats || null,
                    ageLimits: data.regulation?.ageLimits || '0+',
                },
            });
        } catch (err) {
            console.error('[EditBattleForm] Failed to fetch battle data:', err.message);
            setError('Не удалось загрузить данные баттла');
        } finally {
            setFetchLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleCoverChange = (e) => {
        const file = e?.target?.files?.[0];
        if (!file) {
            console.warn('[EditBattleForm] No file selected');
            return;
        }

        console.log('[EditBattleForm] File selected:', file.name, file.type, file.size);

        // Проверка размера (5 MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Размер файла не должен превышать 5 МБ');
            return;
        }

        // Проверка типа
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Допустимые форматы: JPEG, PNG, WebP');
            return;
        }

        // Создаём preview
        const previewUrl = URL.createObjectURL(file);

        setFormData((prev) => ({
            ...prev,
            coverFile: file,
            coverPreviewUrl: previewUrl,
        }));
        setError(null);
    };

    const handleRemoveCover = async () => {
        try {
            setLoading(true);
            // Удаляем обложку на сервере
            await battlesApi.deleteCover(battleId);

            setFormData((prev) => ({
                ...prev,
                coverImageId: null,
                coverFile: null,
                coverPreviewUrl: null,
            }));

            // Обновляем данные баттла
            await fetchBattleData();
        } catch (err) {
            console.error('[EditBattleForm] Failed to delete cover:', err.message);
            setError('Не удалось удалить обложку');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        if (!formData.title || !formData.title.trim()) {
            setError('Название баттла обязательно');
            return false;
        }
        if (!formData.description || !formData.description.trim()) {
            setError('Описание обязательно');
            return false;
        }
        if (!formData.startTime) {
            setError('Дата начала обязательна');
            return false;
        }
        if (!formData.endTime) {
            setError('Дата окончания обязательна');
            return false;
        }
        if (formData.startTime && formData.endTime && new Date(formData.startTime) >= new Date(formData.endTime)) {
            setError('Дата начала должна быть раньше даты окончания');
            return false;
        }
        if (formData.maxParticipants && isNaN(parseInt(formData.maxParticipants))) {
            setError('Максимальное количество участников должно быть числом');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            const payload = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                type: formData.type,
                format: formData.format,
                status: formData.status,
                visibility: formData.visibility,
                ageRestriction: parseInt(formData.ageRestriction) || 0,
                startTime: formData.startTime,
                endTime: formData.endTime,
                registrationDeadline: formData.registrationDeadline || null,
                prizePool: formData.prizePool.trim() || null,
                maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
                coverImageId: formData.coverImageId || null,
                tags: formData.tags || [],
                organizers: formData.organizers || [],
                judges: formData.judges || [],
                participants: formData.participants || [],
                regulation: (formData.regulation.rules.length > 0 || formData.regulation.formats || formData.regulation.ageLimits) ? {
                    rules: formData.regulation.rules,
                    limitations: formData.regulation.limitations,
                    formats: formData.regulation.formats || null,
                    ageLimits: formData.regulation.ageLimits || '0+',
                } : null,
            };

            await battlesApi.update(battleId, payload);

            // Если есть новый файл обложки, загружаем его
            if (formData.coverFile) {
                try {
                    console.log('[EditBattleForm] Uploading cover for battle', battleId, 'File:', formData.coverFile.name);
                    const coverResponse = await battlesApi.uploadCover(battleId, formData.coverFile);
                    console.log('[EditBattleForm] Cover uploaded:', coverResponse);
                    setFormData(prev => ({
                        ...prev,
                        coverImageId: coverResponse.coverImageId,
                        coverFile: null,
                        coverPreviewUrl: null,
                    }));
                } catch (coverErr) {
                    console.error('[EditBattleForm] Failed to upload cover:', coverErr.message);
                    console.error('[EditBattleForm] Error response:', coverErr.response?.data);
                    // Прерываем процесс и показываем ошибку
                    throw new Error(coverErr.response?.data?.message || coverErr.message || 'Не удалось загрузить обложку');
                }
            }

            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess();
                }
                handleClose();
            }, 1500);
        } catch (err) {
            console.error('[EditBattleForm] Failed to update battle:', err.message);
            setError(err.response?.data?.message || 'Не удалось обновить баттл');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Очищаем URL превью
        if (formData.coverPreviewUrl) {
            URL.revokeObjectURL(formData.coverPreviewUrl);
        }

        setFormData({
            title: '',
            description: '',
            type: 'SINGLE',
            format: 'AUDIO',
            status: 'PLANNED',
            visibility: 'PUBLIC',
            ageRestriction: 0,
            startTime: '',
            endTime: '',
            registrationDeadline: '',
            prizePool: '',
            maxParticipants: '',
            coverImageId: null,
            coverFile: null,
            coverPreviewUrl: null,
            tags: [],
            organizers: [],
            judges: [],
            participants: [],
            regulation: {
                rules: [],
                limitations: [],
                formats: null,
                ageLimits: '0+',
            },
        });
        setError(null);
        setSuccess(false);
        setInternalOpen(false);
        if (onClose) {
            onClose();
        }
    };

    if (!internalOpen) return null;

    if (fetchLoading) {
        return (
            <HrnbModal open={true} onClose={handleClose} title="Редактирование баттла" size="large">
                <div className="create-battle-form">
                    <HrnbLoader size="lg" />
                </div>
            </HrnbModal>
        );
    }

    return (
        <HrnbModal open={internalOpen} onClose={handleClose} title="Редактирование баттла" size="large">
            <div className="create-battle-form">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {success && (
                    <HrnbAlert variant="success" onClose={() => setSuccess(false)}>
                        Баттл успешно обновлён!
                    </HrnbAlert>
                )}

                <HrnbForm onSubmit={handleSubmit}>
                    <div className="form-row">
                        <HrnbInput
                            label="Название *"
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', typeof e === 'string' ? e : e.target.value)}
                            placeholder="Введите название баттла"
                            disabled={loading || fetchLoading}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <HrnbTextArea
                            label="Описание *"
                            value={formData.description}
                            onChange={(e) => handleChange('description', typeof e === 'string' ? e : e.target.value)}
                            placeholder="Опишите суть баттла"
                            rows={4}
                            disabled={loading || fetchLoading}
                            required
                        />
                    </div>

                    <div className="form-row form-row-split">
                        <HrnbSelect
                            value={formData.type}
                            onChange={(value) => handleChange('type', value)}
                            options={TYPE_OPTIONS}
                            label="Тип *"
                            placeholder="Выберите тип"
                            disabled={loading || fetchLoading}
                        />

                        <HrnbSelect
                            value={formData.format}
                            onChange={(value) => handleChange('format', value)}
                            options={FORMAT_OPTIONS}
                            label="Формат *"
                            placeholder="Выберите формат"
                            disabled={loading || fetchLoading}
                        />

                        <HrnbSelect
                            value={formData.status}
                            onChange={(value) => handleChange('status', value)}
                            options={STATUS_OPTIONS}
                            label="Статус *"
                            placeholder="Выберите статус"
                            disabled={loading || fetchLoading}
                        />

                        <HrnbSelect
                            value={formData.visibility}
                            onChange={(value) => handleChange('visibility', value)}
                            options={VISIBILITY_OPTIONS}
                            label="Видимость *"
                            placeholder="Выберите видимость"
                            disabled={loading || fetchLoading}
                        />
                    </div>

                    {/* Информация о выбранном формате */}
                    <div className="form-row">
                        <div className={`format-info-block format-info-block--${(formData.format || '').toLowerCase()}`}>
                            <div className="format-info-block__header">
                                <span className="format-info-block__icon">{getFormatIcon(formData.format)}</span>
                                <strong>{getFormatLabel(formData.format)}</strong>
                            </div>
                            <p className="format-info-block__description">
                                {getFormatDescription(formData.format)}
                            </p>
                            <div className="format-info-block__details">
                                <span>📁 {getFormatFileTypes(formData.format).hint}</span>
                                <span>💾 Макс. размер: {Math.round(getFormatFileTypes(formData.format).maxFileSize / 1024 / 1024)} МБ</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-row form-row-split">
                        <HrnbDateTimePicker
                            label="Дата начала *"
                            value={formData.startTime}
                            onChange={(value) => handleChange('startTime', value)}
                            disabled={loading || fetchLoading}
                            placeholder="Выберите дату и время"
                            required
                        />

                        <HrnbDateTimePicker
                            label="Дата окончания *"
                            value={formData.endTime}
                            onChange={(value) => handleChange('endTime', value)}
                            disabled={loading || fetchLoading}
                            placeholder="Выберите дату и время"
                            required
                        />
                    </div>

                    <div className="form-row form-row-split">
                        <HrnbDateTimePicker
                            label="Регистрация до"
                            value={formData.registrationDeadline}
                            onChange={(value) => handleChange('registrationDeadline', value)}
                            disabled={loading || fetchLoading}
                            placeholder="Выберите дату и время"
                        />

                        <HrnbInput
                            label="Макс. участников"
                            type="number"
                            value={formData.maxParticipants}
                            onChange={(e) => handleChange('maxParticipants', typeof e === 'string' ? e : e.target.value)}
                            placeholder="Неограниченно"
                            min="1"
                            disabled={loading || fetchLoading}
                        />
                    </div>

                    <div className="form-row">
                        <HrnbInput
                            label="Призовой фонд"
                            type="text"
                            value={formData.prizePool}
                            onChange={(e) => handleChange('prizePool', typeof e === 'string' ? e : e.target.value)}
                            placeholder="Например: 10000 RUB"
                            disabled={loading || fetchLoading}
                        />
                    </div>

                    <div className="form-row">
                        <label className="hrnb-input-label">Обложка баттла</label>
                        <HrnbFileUpload
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleCoverChange}
                            disabled={loading || fetchLoading}
                            hint="Макс. размер: 5 МБ. Форматы: JPEG, PNG, WebP"
                        />
                        {formData.coverPreviewUrl && (
                            <div className="battle-cover-preview" style={{ marginTop: 12 }}>
                                <HrnbImage
                                    src={formData.coverPreviewUrl}
                                    alt="Предпросмотр обложки"
                                    style={{
                                        maxWidth: 300,
                                        maxHeight: 300,
                                        borderRadius: 8,
                                        objectFit: 'cover',
                                    }}
                                />
                                <HrnbButton
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        coverFile: null,
                                        coverPreviewUrl: null,
                                    }))}
                                    style={{ marginTop: 8 }}
                                    icon={<DeleteIcon/>}
                                >
                                    Удалить обложку
                                </HrnbButton>
                            </div>
                        )}
                        {!formData.coverPreviewUrl && formData.coverImageId && (
                            <div className="battle-cover-preview" style={{ marginTop: 12 }}>
                                <p className="text-muted">Текущая обложка загружена</p>
                                <HrnbButton
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleRemoveCover}
                                    disabled={loading}
                                    icon={<DeleteIcon/>}
                                >
                                    {loading ? <HrnbLoader size="sm" /> : 'Удалить текущую обложку'}
                                </HrnbButton>
                            </div>
                        )}
                    </div>

                    <div className="form-row">
                        <HrnbSelect
                            value={String(formData.ageRestriction)}
                            onChange={(value) => handleChange('ageRestriction', parseInt(value))}
                            options={AGE_RESTRICTION_OPTIONS}
                            label="Возрастное ограничение"
                            placeholder="Выберите возрастное ограничение"
                            disabled={loading || fetchLoading}
                        />
                    </div>

                    <div className="form-row">
                        <HrnbInput
                            label="Форматы (formats)"
                            type="text"
                            value={formData.regulation.formats || ''}
                            onChange={(e) => handleChange('regulation', {
                                ...formData.regulation,
                                formats: typeof e === 'string' ? e : e.target.value || null,
                            })}
                            placeholder="Например: 1v1, 2v2"
                            disabled={loading || fetchLoading}
                        />
                    </div>

                    <div className="form-actions">
                        <HrnbButton variant="outline" onClick={handleClose} disabled={loading || fetchLoading} icon={<ClearIcon/>}>
                            Отмена
                        </HrnbButton>
                        <HrnbButton variant="primary" type="submit" disabled={loading || fetchLoading} icon={<SaveIcon/>}>
                            {loading ? <HrnbLoader size="sm" /> : 'Сохранить изменения'}
                        </HrnbButton>
                    </div>
                </HrnbForm>
            </div>
        </HrnbModal>
    );
}
